import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TodoListItem from "./TodoListItem";
import {
  fetchTodoTasks,
  createTodoTask,
  updateTodoTask,
  clearSuccessMessage,
} from "../../redux/Slices/todoSlice";
import Loader from "../../components/Common/Loader";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import DatePicker from "react-datepicker";
import { Modal } from "antd";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import { Collapse } from "antd";
import { DownOutlined } from "@ant-design/icons";

function Todo() {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  const { profileData } = useSelector((state) => state.profile);
  const { Panel } = Collapse;
  const {
    tomorrowsTaskList,
    todaysTodoTasks,
    overDueTodoTasks,
    loading,
    error,
    successMessage,
    isLoadingTask,
    deletedMessage,
  } = useSelector((state) => state.todo);

  const [toShowTaskPop, setToShowTaskPop] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [selectedForm, setSelectedForm] = useState({
    id: "",
    coachId: "",
    task: "",
    dateOfTask: new Date().toISOString(),
    status: true,
  });

  const coachId = profileData?.coachId;

  useEffect(() => {
    if (coachId) {
      dispatch(fetchTodoTasks(coachId));
    }
  }, [dispatch, coachId]);

  const handleTaskInputChange = (e) => {
    setErrors({});
    const { value } = e.target;
    setSelectedForm((prevState) => ({
      ...prevState,
      task: value,
    }));
  };

  const handleTaskDateChange = (date) => {
    setErrors({});

    const formattedDate = date?.toISOString();

    setSelectedForm((prevState) => ({
      ...prevState,
      dateOfTask: formattedDate,
    }));
  };

  const handleAddTask = () => {
    setErrors({});
    const newErrors = {};
    if (!selectedForm.task.trim()) {
      newErrors.taskName = trans("toDo.taskNameRequired");
      setErrors(newErrors);
    }
    if (!selectedForm.dateOfTask) {
      newErrors.taskDate = trans("toDo.taskDateRequired");
      setErrors(newErrors);
    }

    if (selectedForm.dateOfTask) {
      const selectedDate = new Date(selectedForm.dateOfTask);
      const today = new Date();
      const oneMonthLater = new Date(today);
      oneMonthLater.setMonth(today.getMonth() + 1);
      if (selectedDate > oneMonthLater) {
        newErrors.taskDate = trans("toDo.taskDateTooFar");
        setErrors(newErrors);
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (selectedForm.task !== "") {
      const newTask = {
        coachId: coachId,
        task: selectedForm.task,
        dateOfTask: selectedForm.dateOfTask,
        status: false,
      };
      if (selectedForm.id !== "") {
        dispatch(updateTodoTask(selectedForm)).then(() => {
          dispatch(fetchTodoTasks(coachId));
        });
      } else {
        dispatch(createTodoTask(newTask)).then(() => {
          dispatch(fetchTodoTasks(coachId));
        });
      }

      setToShowTaskPop(false);
      setSelectedForm({
        id: "",
        coachId: "",
        task: "",
        dateOfTask: new Date().toISOString(),
        status: false,
      });
    }
    setErrors({});
  };

  const handleCancelTaskModal = () => {
    setErrors({});
    setToShowTaskPop(false);
    setSelectedForm({
      id: "",
      coachId: "",
      task: "",
      dateOfTask: new Date().toISOString(),
      status: false,
    });
  };

  const handlePanelChange = (key) => {
    if (activeKey === key) {
      setActiveKey(null);
    } else {
      setActiveKey(key);
    }
  };

  const handleEditTask = (item) => {
    setErrors({});
    setSelectedForm({
      id: item.taskId,
      coachId: item.coachId,
      task: item.task,
      dateOfTask: item.dateOfTask,
      status: item.status,
    });
    setToShowTaskPop(true);
  };
  const handleTaskSelectionToggle = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  useEffect(() => {
    if (deletedMessage) {
      dispatch(clearSuccessMessage());
      dispatch(fetchTodoTasks(coachId));
    }
  }, [deletedMessage, coachId, dispatch]);

  useEffect(() => {
    if (successMessage) {
      dispatch(clearSuccessMessage());
      dispatch(fetchTodoTasks(coachId));
    }
  }, [successMessage, dispatch, coachId]);

  const getDateLabel = (dateString) => {
    const taskDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1, d2) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();

    if (isSameDay(taskDate, today)) return trans("toDo.today");
    if (isSameDay(taskDate, yesterday)) trans("toDo.yesterday");

    return taskDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderGroupedTasks = (tasks) => {
    const groupedTasks = {};
    tasks.forEach((task) => {
      const label = getDateLabel(task.dateOfTask);
      if (!groupedTasks[label]) groupedTasks[label] = [];
      groupedTasks[label].push(task);
    });

    return Object.entries(groupedTasks).map(([label, tasks]) => (
      <div key={label} className="task-group mb-2 ">
        {label !== "Today" && <p className="fs-7">{label}</p>}
        {tasks.map((task) => (
          <TodoListItem
            key={task.taskId}
            item={task}
            isSelected={selectedTasks.includes(task.taskId)}
            onToggleSelection={() => handleTaskSelectionToggle(task.taskId)}
            coachId={coachId}
            handleEditTask={handleEditTask}
          />
        ))}
      </div>
    ));
  };

  return (
    <div className="todo-container  ">
      <div className="row align-items-center mb-3">
        <h4 className="col website-hits-title ">{trans("toDo.todoTask")}</h4>
        <div className="col text-end">
          <button
            className="btn todo-mark-as-read-btn create-task-btn"
            onClick={() => setToShowTaskPop(!toShowTaskPop)}
          >
            {trans("toDo.createTask")}
          </button>
        </div>
      </div>

      <div className="row align-items-center mb-3">
        <div className="col-auto"></div>
      </div>

      <div className="todo-task-list scrollable-container">
        {isLoadingTask || loading ? (
          <Loader />
        ) : error ? (
          <p>{error}</p>
        ) : todaysTodoTasks.length === 0 &&
          tomorrowsTaskList.length === 0 &&
          overDueTodoTasks.length === 0 ? (
          <p className="no-tasks-message"> {trans("toDo.noTasks")}</p>
        ) : (
          <Collapse activeKey={activeKey} accordion>
            <Panel
              className="Panel-workout-accordion"
              key="1"
              header={
                <div
                  className=" heading-todo-task d-flex justify-content-between "
                  onClick={() => handlePanelChange("1")}
                >
                  {trans("toDo.todaysTask")}
                  <div>
                    <DownOutlined className="down-arrow-accordion" />
                  </div>
                </div>
              }
              showArrow={false}
            >
              {todaysTodoTasks.length === 0 ? (
                <p>{trans("toDo.noTasksforToday")}</p>
              ) : (
                renderGroupedTasks(todaysTodoTasks)
              )}
            </Panel>

            <Panel
              className="Panel-workout-accordion"
              key="2"
              header={
                <div
                  className=" heading-todo-task d-flex justify-content-between"
                  onClick={() => handlePanelChange("2")}
                >
                  <span>{trans("toDo.UpcomingTask")}</span>
                  <div>
                    <DownOutlined className="down-arrow-accordion" />
                  </div>
                </div>
              }
              showArrow={false}
            >
              {tomorrowsTaskList.length === 0 ? (
                <p>{trans("toDo.noUpcomingTasks")}</p>
              ) : (
                renderGroupedTasks(tomorrowsTaskList)
              )}
            </Panel>
            <Panel
              className="Panel-workout-accordion"
              key="3"
              header={
                <div
                  className=" heading-todo-task d-flex justify-content-between"
                  onClick={() => handlePanelChange("3")}
                >
                  {trans("toDo.overdueTask")}
                  <div>
                    <DownOutlined className="down-arrow-accordion" />
                  </div>
                </div>
              }
              showArrow={false}
            >
              {overDueTodoTasks.length ? (
                renderGroupedTasks(overDueTodoTasks)
              ) : (
                <p>{trans("toDo.noOverdueTasks")}</p>
              )}
            </Panel>
          </Collapse>
        )}
      </div>

      <Modal
        className="app-font-proxima"
        title={<div></div>}
        open={toShowTaskPop}
        onCancel={handleCancelTaskModal}
        onOk={handleAddTask}
        okButtonProps={{
          className: "create-button",
        }}
        cancelButtonProps={{
          className: "cancel-button",
        }}
      >
        <div className="">
          <div className=" mt-4 col">
            <FormLabel label={"Task"} required />
            <div className="w-100 ">
              <FormControl
                type="text"
                value={selectedForm.task}
                className="form-control rounded-3 h-10 task-input-todo"
                placeholder="Add Task..."
                onChange={handleTaskInputChange}
                isInvalid={!!errors.taskName}
                feedback={errors.taskName}
              />
            </div>

            <div className="mt-1">
              <FormLabel label={trans("ClientWorkout.date")} required />
              <div className="d-flex flex-column">
                <div className="position-relative date-picker fit-width-reminder">
                  <DatePicker
                    calendarClassName="my-task-datepicker"
                    className={`custom-datepicker w-100 ${
                      errors.taskDate
                        ? "custom-datepicker w-100 datepicker-width-reminder-invalid"
                        : "custom-datepicker w-100 datepicker-width-reminder"
                    }`}
                    selected={
                      selectedForm.dateOfTask
                        ? new Date(selectedForm.dateOfTask)
                        : new Date()
                    }
                    onChange={handleTaskDateChange}
                    dateFormat="MMM dd, yyyy"
                    placeholderText={trans("ClientWorkout.selectDate")}
                    minDate={new Date()}
                    dayClassName={(date) => {
                      const selectedDate = selectedForm.dateOfTask
                        ? new Date(selectedForm.dateOfTask)
                        : new Date();
                      return date.toDateString() === selectedDate.toDateString()
                        ? "inline-selected-day"
                        : undefined;
                    }}
                  />
                  <style>
                    {`
    .inline-selected-day {
      background-color: #383D8D !important; 
      color: white !important;
    }
  `}
                  </style>
                  <i className="bi bi-calendar3 calendar-icon"></i>
                </div>
              </div>
              {errors.taskDate && (
                <div className="invalid-feedback d-block">
                  {errors.taskDate}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Todo;
